import { BuildingLibraryIcon } from "@heroicons/react/24/solid";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { MoneyInput } from "@/core/components/money-input";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { parseMaskedDollarInput } from "@/core/utils/money";
import {
  useDepositWithdraw,
  useGetAchAccount,
} from "@/internal-transactions/query";
import volcaBlackWhite from "@/assets/images/volca-black-white.png";

const schema = Yup.object().shape({
  amount: Yup.number()
    .transform((_, orig) => {
      if (orig === "" || orig === null || orig === undefined) return undefined;
      const parsed = parseMaskedDollarInput(orig);
      return parsed !== null ? parsed : undefined;
    })
    .required("Amount is required")
    .positive("Amount must be positive")
    .min(1, "Amount must be at least 1"),
});

interface DepositWithdrawModalProps {
  mode?: "deposit" | "withdraw";
  onOpenChange: (open: boolean) => void;
  businessId?: string | null;
}

const DepositWithdrawModal = ({
  mode,
  onOpenChange,
  businessId,
}: DepositWithdrawModalProps) => {
  const isDeposit = mode === "deposit";

  const { data: achAccount } = useGetAchAccount(businessId);
  const { mutate: depositWithdraw } = useDepositWithdraw(isDeposit, businessId);

  return (
    <Dialog open={!!mode} onOpenChange={onOpenChange}>
      <DialogContent
        description={isDeposit ? "Deposit funds" : "Withdraw funds"}
      >
        <Formik
          initialValues={{
            amount: "",
          }}
          validationSchema={schema}
          validateOnMount={true}
          onSubmit={(values, { setSubmitting }) => {
            const parsed = parseMaskedDollarInput(String(values.amount ?? ""));
            const amountCents = parsed !== null ? Math.round(parsed * 100) : 0;

            return depositWithdraw(
              { amount: amountCents },
              {
                onSuccess: () => {
                  toast(
                    `${isDeposit ? "Deposit" : "Withdrawal"} successfully initiated`,
                  );
                  setSubmitting(false);
                  onOpenChange(false);
                },
              },
            );
          }}
        >
          {({
            isSubmitting,
            isValid,
            errors,
            touched,
            values,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form>
              <DialogHeader className="border border-stroke-tertiary text-left">
                <DialogTitle>
                  {isDeposit ? "Deposit funds" : "Withdraw funds"}
                </DialogTitle>
              </DialogHeader>

              <Stack className="p-6" gap="lg" fullWidth>
                <Stack gap="lg">
                  <Stack direction="row" gap="md" align="center">
                    {isDeposit ? (
                      <BuildingLibraryIcon className="h-6 w-6 text-tertiary-text" />
                    ) : (
                      <img
                        src={volcaBlackWhite}
                        alt="QR Code"
                        className="h-6 w-6"
                      />
                    )}
                    <Stack gap="xs">
                      <Typography variant="subtitleW3" textColor="tertiary">
                        From
                      </Typography>
                      <Typography variant="bodyW2">
                        {isDeposit
                          ? `${achAccount?.name} - ${achAccount?.mask}`
                          : "Volca"}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" gap="md" align="center">
                    {isDeposit ? (
                      <img
                        src={volcaBlackWhite}
                        alt="QR Code"
                        className="h-6 w-6"
                      />
                    ) : (
                      <BuildingLibraryIcon className="h-6 w-6 text-tertiary-text" />
                    )}
                    <Stack gap="xs">
                      <Typography variant="subtitleW3" textColor="tertiary">
                        To
                      </Typography>
                      <Typography variant="bodyW2">
                        {isDeposit
                          ? "Volca"
                          : `${achAccount?.name} - ${achAccount?.mask}`}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

                <MoneyInput
                  label={`Amount to ${isDeposit ? "deposit" : "withdraw"}`}
                  error={
                    touched.amount && values.amount ? errors.amount : undefined
                  }
                  name="amount"
                  value={values.amount || ""}
                  onChange={(value) => setFieldValue("amount", value)}
                  onBlur={() => setFieldTouched("amount", true)}
                  required
                  fullWidth
                  autoFocus
                />

                <Typography variant="bodyW2" textColor="tertiary">
                  Your funds will arrive in 3-5 business days
                </Typography>
              </Stack>

              <DialogFooter>
                <Button
                  variant="tertiary"
                  type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid}
                  loading={isSubmitting}
                  className="min-w-[108px]"
                >
                  {isDeposit ? "Deposit" : "Withdraw"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default DepositWithdrawModal;
